import React, { useState, useEffect } from 'react';
import { Button, Table, Container, Row, Col, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { searchClients, deleteClient, patchClient, logError } from '../api';
import Pagination from './Global/Pagination';
import ClientSearchForm from './Global/ClientSearchForm';
import InfoToggle from './Global/InfoToggle';
import DisabledToggle from './ui/DisabledToggle';
import DisabledRowStyles from './ui/DisabledRowStyles';
import SortableHeader from './ui/SortableHeader';
import TotalCount from './Global/TotalCount';
import { useDisabledToggle } from '../hooks/useDisabledToggle';
import { useSorting } from '../hooks/useSorting';
import useFilterPersistence from '../hooks/useFilterPersistence';
import { getDisabledRowClass, createToggleButton } from '../utils/disabledUtils';
import { getSalesRepId, isPropietarioOrAdminOrGestion, isJefeComerciales, isComercialAndNotOwnerAdminGestion } from '../services/AuthService';

const Clients = () => {
    const [clients, setClients] = useState([]);
    const [totalClients, setTotalClients] = useState(0);
    const [loading, setLoading] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [clientToDelete, setClientToDelete] = useState(null);
    const [filters, setFilters] = useFilterPersistence('filters_clients', {});
    const [pagination, setPagination] = useFilterPersistence('pagination_clients', { page: 1, pageSize: undefined });
    const currentPage = pagination.page;
    const clientsPerPage = pagination.pageSize;
    const [openSearchForm, setOpenSearchForm] = useState(false); // Estado para controlar si el formulario de búsqueda está abierto
    const { orderBy, orderDesc, handleSort, getSortIcon } = useSorting();
    const { includeDisabled, setIncludeDisabled } = useDisabledToggle();
    const navigate = useNavigate();

    const currentSalesRepId = Number(getSalesRepId());

    // Cargar la lista de clientes desde la API con paginación y filtros
    useEffect(() => {
        const fetchClients = async () => {
            setLoading(true);
            try {
                const response = (await searchClients(filters, currentPage, clientsPerPage, includeDisabled, orderBy, orderDesc)).data;
                if (response?.items && Array.isArray(response.items)) {
                    setClients(response.items);
                    setTotalClients(response.totalCount);
                } else {
                    console.error('Unexpected response format:', response);
                }
            } catch (error) {
                logError(error);
                console.error('Error fetching clients:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchClients();
    }, [currentPage, clientsPerPage, filters, includeDisabled, orderBy, orderDesc]);

    // Manejo de eliminación de un cliente
    const handleDelete = (id) => {
        setClientToDelete(id);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        try {
            if (clientToDelete) {
                await deleteClient(clientToDelete);
                setClients((prevClients) => prevClients.filter((client) => client.id !== clientToDelete));
                setShowDeleteModal(false);
            }
        } catch (error) {
            logError(error);
            console.error('Error deleting client:', error);
        }
    };

    const cancelDelete = () => {
        setShowDeleteModal(false);
        setClientToDelete(null);
    };

    // Manejo de habilitar/deshabilitar cliente
    const handleToggleStatus = async (clientId, currentStatus) => {
        try {
            await patchClient(clientId, { id: clientId, disabled: !currentStatus });
            setClients((prevClients) => 
                prevClients.map((client) => 
                    client.id === clientId 
                        ? { ...client, disabled: !currentStatus }
                        : client
                )
            );
        } catch (error) {
            logError(error);
            console.error('Error toggling client status:', error);
        }
    };

    const handlePageChange = (page) => {
        setPagination({ ...pagination, page });
    };

    const handleItemsPerPageChange = (event) => {
        setPagination({ page: 1, pageSize: Number(event) });
    };

    const handleToggleSearchForm = () => {
        setOpenSearchForm(!openSearchForm);
    };

    return (
        <>
            <DisabledRowStyles />
            <Container fluid className="my-4">
            <Row className="mb-4">
                <Col xs={12} md={6}>
                    <h1>Clientes</h1>
                </Col>
                <Col xs={12} md={6} className="d-flex justify-content-end align-items-center">
                    <DisabledToggle 
                        includeDisabled={includeDisabled}
                        onChange={setIncludeDisabled}
                    />
                    <Button
                        variant="primary"
                        onClick={() => navigate('/clients/new')}
                    >
                        Nuevo cliente
                    </Button>
                </Col>
            </Row>

            <Row className="mb-4">
                <Col xs={12}>
                    <InfoToggle
                        label="Buscar Clientes"
                        openState={openSearchForm}
                        handleToggle={handleToggleSearchForm}
                        showChevron={true}
                        color="#007bff"
                    />
                    {openSearchForm && (
                        <div className="mt-3">
                            <ClientSearchForm
                                initialFilters={filters}
                                onChangeFilters={(newFilters) => {
                                    setFilters(newFilters);
                                    setPagination({ ...pagination, page: 1 });
                                }}
                                includeDisabled={includeDisabled}
                            />
                        </div>
                    )}
                </Col>
            </Row>

            <div style={{ overflowX: 'auto' }}>
                {!loading && (
                    <div className="d-flex justify-content-end mb-2">
                        <TotalCount totalCount={totalClients} />
                    </div>
                )}
                <Table striped bordered hover responsive="sm">
                    <thead>
                        <tr>
                            <SortableHeader column="id" onSort={handleSort} getSortIcon={getSortIcon}>ID</SortableHeader>
                            {(!isComercialAndNotOwnerAdminGestion() || isJefeComerciales()) && <SortableHeader column="salesRep.name" onSort={handleSort} getSortIcon={getSortIcon}>Comercial</SortableHeader>}
                            <SortableHeader column="dni" onSort={handleSort} getSortIcon={getSortIcon}>DNI</SortableHeader>
                            <SortableHeader column="name" onSort={handleSort} getSortIcon={getSortIcon}>Nombre</SortableHeader>
                            <SortableHeader column="lastName1" onSort={handleSort} getSortIcon={getSortIcon}>Apellido 1</SortableHeader>
                            <SortableHeader column="lastName2" onSort={handleSort} getSortIcon={getSortIcon}>Apellido 2</SortableHeader>
                            <SortableHeader column="phone" onSort={handleSort} getSortIcon={getSortIcon}>Teléfono</SortableHeader>
                            <SortableHeader column="email" onSort={handleSort} getSortIcon={getSortIcon}>Email</SortableHeader>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {clients.length > 0 ? (
                            clients.map((client) => (
                                <tr key={client.id} className={getDisabledRowClass(client.disabled)}>
                                    <td>{client.id}</td>
                                    {(!isComercialAndNotOwnerAdminGestion() || isJefeComerciales()) && <td>{client.salesRep.name} {client.salesRep.lastName1}</td>}
                                    <td>{client.dni}</td>
                                    <td>{client.name}</td>
                                    <td>{client.lastName1}</td>
                                    <td>{client.lastName2}</td>
                                    <td>{client.phone}</td>
                                    <td>{client.email}</td>
                                    {(isPropietarioOrAdminOrGestion() || client.salesRepId === currentSalesRepId) ? (
                                        <td className="d-flex justify-content-start">
                                            <Button
                                                variant="warning"
                                                onClick={() => navigate(`/clients/edit/${client.id}`)}
                                                size="sm"
                                                className="me-2"
                                            >
                                                Editar
                                            </Button>
                                            {(() => {
                                                const toggleProps = createToggleButton(
                                                    client.disabled, 
                                                    () => handleToggleStatus(client.id, client.disabled)
                                                );
                                                return (
                                                    <Button
                                                        variant={toggleProps.variant}
                                                        onClick={toggleProps.onClick}
                                                        size={toggleProps.size}
                                                        className={toggleProps.className}
                                                    >
                                                        {toggleProps.text}
                                                    </Button>
                                                );
                                            })()}
                                            <Button
                                                variant="danger"
                                                onClick={() => handleDelete(client.id)}
                                                size="sm"
                                            >
                                                Borrar
                                            </Button>
                                        </td>
                                    ) : (
                                        <td className="text-muted" style={{ fontStyle: 'italic' }}>
                                            Sin permisos
                                        </td>
                                )}

                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="8" className="text-center">
                                    No se encontraron clientes.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            <Pagination
                items={clients}
                totalCount={totalClients}
                currentPage={currentPage}
                itemsPerPage={clientsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
            />

            <Modal show={showDeleteModal} onHide={cancelDelete}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirmar Eliminación</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>¿Estás seguro de que quieres eliminar este cliente?</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={cancelDelete}>
                        No, Mantener
                    </Button>
                    <Button variant="danger" onClick={confirmDelete}>
                        Sí, Eliminar
                    </Button>
                </Modal.Footer>
            </Modal>
            </Container>
        </>
    );
};

export default Clients;
